import { createFileRoute, Link, useRouter } from '@tanstack/react-router';
import { emitMessage, redirectIfGameLoaded, rejoinEvent, uploadAvatar } from 'engine/api';
import { SkinLogo } from 'components/SkinLogo';
import { Input } from 'components/Input';
import { Button } from 'components/Button';
import { Spacer } from 'components/Spacer';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRef, useState } from 'react';
import { useStore } from 'engine/store';
import { MobileScreen } from 'components/MobileScreen';
import { AvatarUpload } from 'components/AvatarUpload';
import { networkLanguageLoader, setLanguage } from 'engine/translate';
import { Heading } from 'components/Heading';
import { Para } from 'components/Para';
import { setTag as sentrySetTag, setUser as sentrySetUser } from '@sentry/browser';
import { appConfig } from 'engine/appConfig';

export const Route = createFileRoute('/')({
	component: Index,
	beforeLoad: redirectIfGameLoaded,
	loader: async () => emitMessage({ kind: 'variant-list' }),
});

function Index() {
	const [validCode, setValidCode] = useState<string | null>(null);
	const [screen, setScreen] = useState<'code' | 'team' | 'failed-to-join'>('code');

	// Function to handle successful code validation
	const handleCodeValidated = (code: string) => {
		setValidCode(code);
		setScreen('team');
	};

	return (
		<MobileScreen className="flex h-full flex-col items-center gap-2">
			<div className="mt-8"></div>
			<SkinLogo />
			<Spacer />
			<div className="mx-auto w-full max-w-md">
				{screen == 'code' && <CodeForm onCodeValidated={handleCodeValidated} />}
				{screen == 'team' && <TeamNameForm code={validCode!} onJoinError={() => setScreen('failed-to-join')} />}
				{screen == 'failed-to-join' && (
					<div className="flex flex-col gap-4">
						<Heading>Failed to join.</Heading>
						<Para>This may be due to connectivity issues or a problem with the server, please try again later.</Para>
						<Para>If the problem persists, please contact the game master.</Para>
						<Button onClick={() => setScreen('code')}>Try Again</Button>
					</div>
				)}
			</div>
			<Spacer />
			{screen == 'code' && (
				<Link className="font-bold" to="/about" viewTransition={{ types: ['slide-left'] }}>
					{__`About ${appConfig.title}`}
				</Link>
			)}
		</MobileScreen>
	);
}

function CodeForm({ onCodeValidated }: { onCodeValidated: (code: string) => void }) {
	const CodeSchema = z.object({
		code: z.string().min(3),
	});

	type Code = z.infer<typeof CodeSchema>;

	const setSkin = useStore((s) => s.setSkin);
	// const setFreezeState = useStore((s) => s.setFreezeState);
	const setDeviceId = useStore((s) => s.setDeviceId);
	const addDevice = useStore((s) => s.addDevice);

	const router = useRouter();

	const {
		register,
		handleSubmit,
		setError,
		formState: { errors },
	} = useForm<Code>({
		resolver: zodResolver(CodeSchema),
	});

	const onSubmit = async (data: Code) => {
		console.log(data);
		const res = await emitMessage({ kind: 'event-joinable', code: data.code });
		if (!res.joinable) {
			setError('code', { message: `"${data.code.toUpperCase()}" ${res.reason || 'cannot be joined'}.` });
			return;
		}
		if (!res.deviceId) throw new Error('No device ID');
		setDeviceId(res.deviceId);
		addDevice(res.deviceId);

		if (res.language) await setLanguage(res.language, networkLanguageLoader);
		if (res.skin) setSkin(res.skin);
		if (res.rejoin) {
			console.log('Should rejoin');
			sentrySetTag('rejoinEvent', res.eventId);
			sentrySetUser({
				id: res.teamId,
				username: res.teamName,
			});
			if (res.eventId && res.teamId && res.teamName) {
				rejoinEvent({ eventId: res.eventId, teamId: res.teamId, teamName: res.teamName, deviceId: res.deviceId });
				router.navigate({ to: '/rounds', viewTransition: { types: ['slide-up'] } });
				// router.navigate({ to: '/rounds' });
			} else {
				console.log(res);
				throw new Error('cannot rejoin');
			}
		} else {
			onCodeValidated(data.code);
		}
	};

	return (
		<>
			<label htmlFor="" className="mb-2 block text-center text-lg font-medium">
				{__`Enter your code`}
			</label>
			<form className="flex w-full flex-col gap-2" onSubmit={handleSubmit(onSubmit)}>
				<Input
					style={{ width: '100%', textAlign: 'center' }}
					{...register('code')}
					autoFocus={true}
					autoComplete="off"
					placeholder={__`Enter event code`}
					className="uppercase placeholder:normal-case"
				/>
				<Button className="w-full" type="submit">
					{__`Check code`}
				</Button>
				{errors.code && <span className="text-red-500">{errors.code.message}</span>}
			</form>
		</>
	);
}

function TeamNameForm({ code, onJoinError }: { code: string; onJoinError: () => void }) {
	const submittedRef = useRef(false);

	const TeamNameSchema = z.object({
		teamName: z.string().min(1, 'Team name is required'),
		avatar: z
			.any()
			.transform((val) => (val instanceof FileList ? val[0] : val))
			.refine((file) => !file || file instanceof File, {
				message: 'Invalid file',
			})
			.optional(),
	});

	type TeamNameFormData = z.infer<typeof TeamNameSchema>;

	const router = useRouter();
	const initGame = useStore((s) => s.init);

	const {
		register,
		handleSubmit,
		setValue,
		setError,
		watch,
		formState: { errors },
	} = useForm<TeamNameFormData>({
		resolver: zodResolver(TeamNameSchema),
	});

	const teamName = watch('teamName');
	const deviceId = useStore((s) => s.deviceId);

	const onSubmit = async (data: TeamNameFormData) => {
		if (submittedRef.current) {
			console.log('already submitted');
			return;
		}
		console.log(data);
		submittedRef.current = true;

		const result = await emitMessage({
			kind: 'event-join',
			code: code,
			teamName: data.teamName,
			deviceId: deviceId as string,
		});

		if (result.success) {
			const init = result.init!;
			sentrySetTag('event', init.eventId);
			sentrySetUser({
				id: init.teamId,
				username: data.teamName,
			});

			initGame(init);

			if (data.avatar) {
				console.log('uploading avatar', data.avatar);
				uploadAvatar(data.avatar, init.eventId, init.teamId);
			}

			router.navigate({ to: '/intro', viewTransition: { types: ['slide-up'] } });
		} else if (result.msg == 'team-name-taken') {
			setError('teamName', { message: __`Team name '${teamName}' is already taken` });
			setValue('teamName', '');
			submittedRef.current = false;
		} else {
			onJoinError();
		}
		console.log(result);
	};

	return (
		<form className="flex w-full flex-col gap-2" onSubmit={handleSubmit(onSubmit)}>
			<AvatarUpload setValue={setValue} />
			<label className="mb-2 block text-center text-lg font-medium">{__`Enter your team name`}</label>
			<Input
				style={{ width: '100%', textAlign: 'center' }}
				{...register('teamName')}
				autoComplete="off"
				placeholder={__`Team name`}
				autoFocus
			/>
			<Button className="w-full" type="submit">
				{__`Join event`}
			</Button>
			{errors.teamName && <span className="text-red-500">{errors.teamName.message}</span>}
		</form>
	);
}
